import {
  useId,
  useState,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from "react";

import { AlertIcon } from "./icons";

/* ==========================================================================
   One form-field contract for every Shoku site.

   The association between a label, its hint, its error and the control is
   wired by id — never left to visual proximity — and an error is text plus an
   underline plus an icon, never a red box (R8).
   ========================================================================== */

/* Every optional prop here spells `| undefined` out.
 *
 * These are PASS-THROUGH props: a caller writes `hint={maybeHint}` and
 * `error={errors.email ?? null}`, and under `exactOptionalPropertyTypes` — which
 * the user site turns on — "may be absent" and "may be undefined" are different
 * types. Without the union every one of those call sites is an error, and the
 * only alternatives are a conditional spread at each of them or turning the flag
 * off, which is worse than three words here. */
interface FieldShellProps {
  label: string;
  hint?: string | undefined;
  error?: string | null | undefined;
  required?: boolean | undefined;
  className?: string | undefined;
  children: (props: {
    id: string;
    describedBy?: string | undefined;
    invalid: boolean;
  }) => ReactNode;
}

export function Field({ label, hint, error, required, className = "", children }: FieldShellProps) {
  const id = useId();
  const hintId = hint ? `${id}-hint` : undefined;
  const errorId = error ? `${id}-error` : undefined;
  const describedBy = [hintId, errorId].filter(Boolean).join(" ") || undefined;

  return (
    <div className={`field ${className}`.trim()}>
      <label className="field__label" htmlFor={id}>
        {label}
        {required ? (
          <span className="required" aria-hidden="true">
            {" *"}
          </span>
        ) : null}
      </label>
      {children({ id, describedBy, invalid: Boolean(error) })}
      {hint ? (
        <span className="field__hint" id={hintId}>
          {hint}
        </span>
      ) : null}
      {error ? (
        <span className="field__error" id={errorId} role="alert">
          <AlertIcon />
          {error}
        </span>
      ) : null}
    </div>
  );
}

/**
 * The `Field` recipe for a group of controls instead of one: a `<fieldset>`
 * with a legend, then the caller's own markup, then the same hint/error tail
 * `Field` renders, in the same order, with the same icon.
 */
export function FieldGroup({
  legend,
  hint,
  error,
  required,
  className = "",
  children,
}: {
  legend: string;
  hint?: string | undefined;
  error?: string | null | undefined;
  required?: boolean | undefined;
  className?: string | undefined;
  children: ReactNode;
}) {
  const id = useId();
  const hintId = hint ? `${id}-hint` : undefined;
  const errorId = error ? `${id}-error` : undefined;
  const describedBy = [hintId, errorId].filter(Boolean).join(" ") || undefined;

  return (
    <fieldset
      className={`field ${className}`.trim()}
      data-invalid={error ? "true" : undefined}
      aria-describedby={describedBy}
      aria-invalid={error ? true : undefined}
    >
      <legend className="field__label">
        {legend}
        {required ? (
          <span className="required" aria-hidden="true">
            {" *"}
          </span>
        ) : null}
        {required ? <span className="sr-only"> (required)</span> : null}
      </legend>
      {children}
      {hint ? (
        <span className="field__hint" id={hintId}>
          {hint}
        </span>
      ) : null}
      {error ? (
        <span className="field__error" id={errorId} role="alert">
          <AlertIcon aria-hidden="true" />
          {error}
        </span>
      ) : null}
    </fieldset>
  );
}

type TextFieldProps = Omit<InputHTMLAttributes<HTMLInputElement>, "id"> & {
  label: string;
  hint?: string | undefined;
  error?: string | null | undefined;
};

export function TextField({ label, hint, error, required, className = "", ...rest }: TextFieldProps) {
  return (
    <Field label={label} hint={hint} error={error} required={required}>
      {({ id, describedBy, invalid }) => (
        <input
          {...rest}
          id={id}
          className={`input ${className}`.trim()}
          required={required}
          aria-invalid={invalid || undefined}
          aria-describedby={describedBy}
        />
      )}
    </Field>
  );
}

/**
 * A password field whose reveal toggle is anchored to the INPUT ROW.
 *
 * Anchoring it to the field (label included) floats the button up into the gap
 * above the input the moment the label wraps — the exact bug the user site
 * shipped.
 */
export function PasswordField({
  label,
  hint,
  error,
  required,
  className = "",
  ...rest
}: Omit<TextFieldProps, "type">) {
  const [revealed, setRevealed] = useState(false);

  return (
    <Field label={label} hint={hint} error={error} required={required}>
      {({ id, describedBy, invalid }) => (
        <div className="input-row">
          <input
            {...rest}
            id={id}
            type={revealed ? "text" : "password"}
            className={`input ${className}`.trim()}
            required={required}
            aria-invalid={invalid || undefined}
            aria-describedby={describedBy}
          />
          <button
            type="button"
            className="password-toggle"
            onClick={() => setRevealed((value) => !value)}
            // The control's own text changes with its state, so it needs no
            // aria-pressed: "Show"/"Hide" already says what it will do.
            aria-controls={id}
          >
            {revealed ? "Hide" : "Show"}
          </button>
        </div>
      )}
    </Field>
  );
}

type TextAreaProps = Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, "id"> & {
  label: string;
  hint?: string | undefined;
  error?: string | null | undefined;
};

export function TextArea({ label, hint, error, required, className = "", ...rest }: TextAreaProps) {
  return (
    <Field label={label} hint={hint} error={error} required={required}>
      {({ id, describedBy, invalid }) => (
        <textarea
          {...rest}
          id={id}
          className={`textarea ${className}`.trim()}
          required={required}
          aria-invalid={invalid || undefined}
          aria-describedby={describedBy}
        />
      )}
    </Field>
  );
}

type SelectFieldProps = Omit<SelectHTMLAttributes<HTMLSelectElement>, "id"> & {
  label: string;
  hint?: string | undefined;
  error?: string | null | undefined;
  children: ReactNode;
};

export function SelectField({
  label,
  hint,
  error,
  required,
  children,
  className = "",
  ...rest
}: SelectFieldProps) {
  return (
    <Field label={label} hint={hint} error={error} required={required}>
      {({ id, describedBy, invalid }) => (
        <select
          {...rest}
          id={id}
          className={`select ${className}`.trim()}
          required={required}
          aria-invalid={invalid || undefined}
          aria-describedby={describedBy}
        >
          {children}
        </select>
      )}
    </Field>
  );
}

/**
 * A drawn checkbox.
 *
 * The control is a real `<input type="checkbox">` with `appearance: none` — not
 * a hidden input behind a decorative span — so every bit of native keyboard,
 * focus and form behaviour survives and only the box is repainted.
 */
export function Checkbox({
  label,
  className = "",
  ...rest
}: Omit<InputHTMLAttributes<HTMLInputElement>, "type"> & { label: ReactNode }) {
  return (
    <label className={`checkbox ${className}`.trim()}>
      <input type="checkbox" {...rest} />
      <span>{label}</span>
    </label>
  );
}

export interface RadioOption<T extends string> {
  value: T;
  label: ReactNode;
  hint?: ReactNode;
  disabled?: boolean;
}

/**
 * A mutually exclusive choice, with radio semantics.
 *
 * The alternative every site reached for — two `aria-pressed` toggle buttons —
 * tells a screen reader there are two independent switches, and tells a sighted
 * user (who has seen the same chips used for multi-select) the same thing.
 */
export function RadioGroup<T extends string>({
  legend,
  name,
  value,
  options,
  onChange,
  disabled = false,
  required,
  hint,
  error,
}: {
  legend: string;
  name: string;
  value: T;
  options: readonly RadioOption<T>[];
  onChange: (value: T) => void;
  disabled?: boolean;
  required?: boolean | undefined;
  hint?: string | undefined;
  error?: string | null | undefined;
}) {
  return (
    <FieldGroup legend={legend} hint={hint} error={error} required={required}>
      <div className="chip-row">
        {options.map((option) => (
          <label key={option.value} className="category-chip category-chip--sm">
            <input
              type="radio"
              name={name}
              value={option.value}
              checked={value === option.value}
              disabled={disabled || option.disabled}
              onChange={() => onChange(option.value)}
            />
            <span>{option.label}</span>
          </label>
        ))}
      </div>
    </FieldGroup>
  );
}

/**
 * A multi-select chip. Emits `.category-chip`, the same class the marketing
 * site's trade picker uses, so "selected" looks identical everywhere.
 */
export function ChipToggle({
  pressed,
  onToggle,
  disabled = false,
  children,
}: {
  pressed: boolean;
  onToggle: () => void;
  disabled?: boolean;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      className="category-chip category-chip--sm"
      aria-pressed={pressed}
      disabled={disabled}
      onClick={onToggle}
    >
      {children}
    </button>
  );
}

/** After a failed submit, put the caret on the first thing that is wrong.
 *  Field errors are wired with aria-describedby, which only speaks when the
 *  field already has focus - so a form that submits with errors and moves
 *  nothing says nothing to a screen reader and, on a phone, leaves the first
 *  error a screen above. rAF so it runs after React has painted the errors. */
export function focusFirstInvalid(form: HTMLFormElement | null | undefined): void {
  requestAnimationFrame(() => {
    const target = form?.querySelector<HTMLElement>(
      '[aria-invalid="true"], [role="group"][aria-describedby] .category-chip, [role="group"][aria-describedby] input',
    );
    target?.focus();
  });
}
